/**
 * HTTP client and typed service layer for frontend-backend interactions.
 * Includes auth, admin, classes, and grades operations with Axios.
 */
import axios, { AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { GradeUploadRequest, ProcessGradesResponse } from '../types/grade.types';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// API client setup with authentication
/** Axios instance configured with base URL and auth header injection. */
const apiClient = axios.create({
  baseURL: API_URL,
});

// Add request interceptor to include auth token
/** Inject `Authorization: Bearer <token>` header when a token is present. */
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Type definitions
export interface LoginResponse {
  id: number;
  username: string;
  is_admin: boolean;
  token: string;
}

export interface ApiKeyResponse {
  service_name: string;
  api_key: string;
}

// Add user management interfaces after existing interfaces
export interface UserResponse {
  id: number;
  username: string;
  is_admin: boolean;
}

export interface UserCreateRequest {
  username: string;
  password: string;
  is_admin: boolean;
}

export interface UserUpdateRequest {
  password?: string;
  is_admin?: boolean;
}

// Authentication services
/** Auth endpoints: login/logout/session and key management. */
export const authService = {
  login: async (username: string, password: string): Promise<LoginResponse> => {
    try {
      const response = await apiClient.post('/api/users/login', { username, password });
      
      // Store token in localStorage
      localStorage.setItem('token', response.data.token);
      
      return response.data;
    } catch (error) {
      console.error('Login error:', error);
      throw new Error('Invalid credentials');
    }
  },
  
  logout: async () => {
    try {
      // Call the backend logout endpoint to invalidate the session
      const token = localStorage.getItem('token');
      if (token) {
        await apiClient.post('/api/users/logout');
      }
    } catch (error) {
      console.error('Error during logout:', error);
    } finally {
      // Always remove the token from localStorage
      localStorage.removeItem('token');
    }
  },
  
  logoutAllSessions: async (): Promise<void> => {
    try {
      // Call the backend endpoint to invalidate all sessions for the current user
      await apiClient.post('/api/users/logout/all');
    } catch (error) {
      console.error('Error during logout from all sessions:', error);
      throw new Error('Failed to logout from all sessions');
    } finally {
      // Always remove the token from localStorage
      localStorage.removeItem('token');
    }
  },
  
  getCurrentUser: async (): Promise<LoginResponse> => {
    try {
      const response = await apiClient.get('/api/users/me');
      return response.data;
    } catch (error) {
      console.error('Error getting current user:', error);
      throw new Error('Not authenticated');
    }
  },
  
  getApiKeys: async (): Promise<ApiKeyResponse[]> => {
    try {
      const response = await apiClient.get('/api/users/api-keys');
      return response.data;
    } catch (error) {
      console.error('Error getting API keys:', error);
      throw new Error('Failed to get API keys');
    }
  },
  
  // Get API keys for a specific user (admin only)
  getUserApiKeys: async (userId: number): Promise<ApiKeyResponse[]> => {
    console.log(`Making API request to get keys for user ID: ${userId}`);
    try {
      const response = await apiClient.get(`/api/users/${userId}/api-keys`);
      console.log(`Received response for user ${userId} API keys:`, response.data);
      return response.data;
    } catch (error) {
      console.error(`Error getting user ${userId} API keys:`, error);
      if (axios.isAxiosError(error)) {
        const status = error.response?.status;
        const message = error.response?.data?.detail || error.message;
        console.error(`Status: ${status}, Message: ${message}`);
      }
      throw new Error('Failed to get user API keys');
    }
  },
  
  updateApiKey: async (serviceName: string, apiKey: string): Promise<ApiKeyResponse> => {
    try {
      const response = await apiClient.put('/api/users/api-keys', {
        service_name: serviceName,
        api_key: apiKey
      });
      
      return response.data;
    } catch (error) {
      console.error('Error updating API key:', error);
      throw new Error('Failed to update API key');
    }
  },
  
  updateUserApiKey: async (userId: number, serviceName: string, apiKey: string): Promise<ApiKeyResponse> => {
    try {
      console.log(`Updating API key for user ${userId}: ${serviceName}`);
      const response = await apiClient.put(`/api/users/${userId}/api-keys`, {
        service_name: serviceName,
        api_key: apiKey
      });
      
      return response.data;
    } catch (error) {
      console.error('Error updating user API key:', error);
      throw new Error('Failed to update user API key');
    }
  },
  
  updateUserApiKeysBatch: async (userId: number, keys: Array<{ service_name: string; api_key: string }>): Promise<void> => {
    try {
      // Assuming the backend endpoint expects a PUT request with the array of keys in the body
      await apiClient.put(`/api/users/${userId}/api-keys/batch`, keys);
    } catch (error) {
      console.error(`Error batch updating API keys for user ${userId}:`, error);
      if (axios.isAxiosError(error)) {
        const status = error.response?.status;
        const message = error.response?.data?.detail || error.message;
        console.error(`Status: ${status}, Message: ${message}`);
        if (status === 403) {
          throw new Error('Permission denied. Only admins can update other users\' API keys.');
        } else if (status === 404) {
           throw new Error('User not found.');
        }
      }
      throw new Error('Failed to update user API keys');
    }
  },
  
  isAuthenticated: (): boolean => {
    return !!localStorage.getItem('token');
  },

  // New user management functions
  /** User CRUD (admin), aligned to backend user routes. */
  
  // Get all users (admin only)
  getAllUsers: async (): Promise<UserResponse[]> => {
    try {
      const response = await apiClient.get('/api/users/all');
      return response.data;
    } catch (error) {
      console.error('Error getting users:', error);
      throw new Error('Failed to get users');
    }
  },
  
  // Create a new user (admin only)
  createUser: async (userData: UserCreateRequest): Promise<UserResponse> => {
    try {
      const response = await apiClient.post('/api/users/create', userData);
      return response.data;
    } catch (error) {
      console.error('Error creating user:', error);
      throw new Error('Failed to create user');
    }
  },
  
  // Update a user (admin only, or self)
  updateUser: async (userId: number, userData: UserUpdateRequest): Promise<UserResponse> => {
    try {
      const response = await apiClient.put(`/api/users/${userId}`, userData);
      return response.data;
    } catch (error) {
      console.error('Error updating user:', error);
      throw new Error('Failed to update user');
    }
  },
  
  // Delete a user (admin only)
  deleteUser: async (userId: number): Promise<void> => {
    try {
      await apiClient.delete(`/api/users/${userId}`);
    } catch (error) {
      console.error('Error deleting user:', error);
      throw new Error('Failed to delete user');
    }
  }
};

export interface StudentMapping {
  lms_id: number;
  wela_id: string;
  full_name: string;
  gender: string;
  grade_level: string;
}

export const studentMappingService = {
  getStudentMappings: async (searchTerm: string = ''): Promise<StudentMapping[]> => {
    try {
      const response = await apiClient.get('/api/students/mappings', {
        params: { search: searchTerm }
      });
      return response.data.mappings;
    } catch (error) {
      console.error('Error fetching student mappings:', error);
      throw new Error('Failed to fetch student mappings');
    }
  }
};

export const adminService = {
  updateStudentDb: async (): Promise<{ status: string, message: string }> => {
    try {
      const response = await apiClient.post('/api/admin/update_student_db');
      return response.data;
    } catch (error: any) {
      console.error('Error updating student database:', error);
      if (axios.isAxiosError(error) && error.response) {
        throw new Error(error.response.data.detail || 'Failed to update student database');
      }
      throw new Error('Failed to update student database');
    }
  },
  
  getDbLastUpdated: async (): Promise<{ last_updated: string | null }> => {
    try {
      const response = await apiClient.get('/api/admin/db_last_updated');
      return response.data;
    } catch (error: any) {
      console.error('Error fetching DB last updated time:', error);
      if (axios.isAxiosError(error) && error.response) {
        throw new Error(error.response.data.detail || 'Failed to fetch DB last updated time');
      }
      throw new Error('Failed to fetch DB last updated time');
    }
  }
};

// API services for WELA and NeoLMS integration
/** Aggregated endpoints used by feature services below. */
export const apiService = {
  // API status checks
  checkNeolmsConnection: async () => {
    try {
      // Use the authenticated apiClient
      const response = await apiClient.get(`/api/check/neolms`);
      return response.data;
    } catch (error) {
      console.error('Error checking NeoLMS connection:', error);
      throw error;
    }
  },
  
  checkWelaConnection: async () => {
    try {
      // Use the authenticated apiClient
      const response = await apiClient.get(`/api/check/wela`);
      return response.data;
    } catch (error) {
      console.error('Error checking WELA connection:', error);
      throw error;
    }
  },
  
  // Class and grade operations
  getClasses: async (schoolYear: string) => {
    try {
      // Use the authenticated apiClient
      const response = await apiClient.get(`/api/wela/classes?school_year=${schoolYear}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching classes:', error);
      throw error;
    }
  },
  
  getClassData: async (className: string) => {
    try {
      // Use the authenticated apiClient
      const response = await apiClient.get(`/api/wela/class/${encodeURIComponent(className)}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching class data:', error);
      throw error;
    }
  },
  
  getGrades: async (classId: number) => {
    try {
      // Use the authenticated apiClient
      const response = await apiClient.get(`/api/neolms/grades?class_id=${classId}`);
      return response.data.grades;
    } catch (error) {
      console.error('Error fetching grades:', error);
      throw error;
    }
  },
  
  processGrades: async (
    className: string,
    neolmsClassId: number,
    quarter: string
  ): Promise<ProcessGradesResponse> => {
    try {
      // Use the authenticated apiClient
      const response = await apiClient.get(
        `/api/grades/process?class_name=${encodeURIComponent(className)}&neolms_class_id=${neolmsClassId}&quarter=${encodeURIComponent(quarter)}`
      );
      return response.data;
    } catch (error) {
      console.error('Error processing grades:', error);
      throw error;
    }
  },
  
  uploadGrades: async (request: GradeUploadRequest) => {
    try {
      // Use the authenticated apiClient
      const response = await apiClient.post(`/api/grades/upload`, request);
      return response.data;
    } catch (error) {
      console.error('Error uploading grades:', error);
      throw error;
    }
  }
};

// Classes service
/** Derived service that composes `apiService` for class flows. */
export const classesService = {
  getSchoolYears: async () => {
    // Generate school years based on current year (±2 years)
    const currentYear = new Date().getFullYear();
    const schoolYears = [];
    
    // Start from 2 years before current year
    for (let i = -2; i <= 2; i++) {
      const startYear = currentYear + i;
      const endYear = startYear + 1;
      schoolYears.push(`SY${startYear}-${endYear}`);
    }
    
    return schoolYears;
  },
  
  getClasses: async (schoolYear: string) => {
    return apiService.getClasses(schoolYear);
  },
  
  getClassDetail: async (className: string) => {
    return apiService.getClassData(className);
  },
};

// Grades service
/** Derived service that composes `apiService` for grade flows. */
export const gradesService = {
  // Get raw grades from NeoLMS
  getNeoLmsGrades: async (classId: number) => {
    return apiService.getGrades(classId);
  },
  
  // Process grades for preview (similar to "Process Grades" button in desktop app)
  processGrades: async (className: string, neolmsClassId: number, quarter: string) => {
    return apiService.processGrades(className, neolmsClassId, quarter);
  },
  
  // Upload processed grades to WELA
  uploadGrades: async (data: GradeUploadRequest) => {
    return apiService.uploadGrades(data);
  }
};

export default apiClient;